<script lang="ts">
	import { Button, Text } from '@Shelly/shelly-design-system';
	import { format } from 'svelte-i18n';
	import { openExternalLink } from 'src/helpers/utilities';
	import { getBackgroundColorImage } from 'src/helpers/background_color_helpers';
	import type {
		DashPromoBannerBackground,
		DashPromoCtaPosition,
	} from 'src/components/Dashboards/dash-promo-types';

	/**
	 * Generic full-bleed dashboard promo banner template.
	 * Background: campaign image, app color token, or solid colour.
	 * CTA position is configurable. Dismiss chrome lives on the gallery.
	 */
	export let title = '';
	export let message = '';
	export let background: DashPromoBannerBackground | undefined;
	/** @deprecated Prefer `background`. Kept for simple image slides. */
	export let image = '';
	export let ctaLabel = '';
	export let ctaPosition: DashPromoCtaPosition = 'bottom-left';
	export let ctaHref = '';
	export let onCtaClick: (() => void) | undefined;
	export let flush = true;
	/** Compact chrome for GlobalInlineBanners (120px). */
	export let compact = false;

	$: resolvedBackground = resolveBackground(background, image);

	const resolveBackground = (
		bg: DashPromoBannerBackground | undefined,
		fallbackImage: string,
	): { style: string; hasImage: boolean } => {
		if (bg?.type === 'image' && bg.src) {
			return {
				style: `background-image: url('${bg.src}'); background-size: cover; background-position: center;`,
				hasImage: true,
			};
		}
		if (bg?.type === 'colorToken') {
			const asset = getBackgroundColorImage(bg.token);
			if (asset?.full) {
				return {
					style: `background-image: url('./${asset.full}'); background-size: cover; background-position: center;`,
					hasImage: true,
				};
			}
		}
		if (bg?.type === 'solid') {
			return { style: `background-color: ${bg.color};`, hasImage: false };
		}
		if (fallbackImage) {
			return {
				style: `background-image: url('${fallbackImage}'); background-size: cover; background-position: center;`,
				hasImage: true,
			};
		}
		return {
			style: 'background: linear-gradient(135deg, #3d7eff 0%, #1a4fd6 100%);',
			hasImage: false,
		};
	};

	const handleCta = () => {
		if (onCtaClick) {
			onCtaClick();
			return;
		}
		if (ctaHref) {
			openExternalLink(ctaHref);
		}
	};
</script>

<div
	class="generic-promo-banner"
	class:generic-promo-banner--flush={flush}
	class:generic-promo-banner--compact={compact}
	style={resolvedBackground.style}
>
	{#if resolvedBackground.hasImage}
		<div class="generic-promo-banner__scrim" aria-hidden="true"></div>
	{/if}

	<div class="generic-promo-banner__body">
		{#if title}
			<Text
				styleType={compact ? 'body-small' : 'h2'}
				class="generic-promo-banner__title text-bold"
			>
				{title}
			</Text>
		{/if}
		{#if message && !compact}
			<Text styleType="body-small" class="generic-promo-banner__message">
				{message}
			</Text>
		{/if}

		{#if ctaLabel || onCtaClick || ctaHref}
			<div
				class="generic-promo-banner__cta-row"
				class:generic-promo-banner__cta-row--bl={ctaPosition === 'bottom-left'}
				class:generic-promo-banner__cta-row--br={ctaPosition === 'bottom-right'}
				class:generic-promo-banner__cta-row--bc={ctaPosition === 'bottom-center'}
				class:generic-promo-banner__cta-row--center={ctaPosition === 'center'}
			>
				<Button
					class="generic-promo-banner__cta"
					size={compact ? 's' : 'm'}
					variant="secondary"
					on:click={handleCta}
				>
					{ctaLabel ||
						$format('button_learn_more', {
							default: 'Learn more',
						})}
				</Button>
			</div>
		{/if}
	</div>
</div>

<style>
	.generic-promo-banner {
		position: relative;
		width: 100%;
		height: 100%;
		min-height: 0;
		max-height: 180px;
		overflow: hidden;
		display: flex;
		flex-direction: column;
		box-sizing: border-box;
	}

	.generic-promo-banner--compact {
		max-height: 120px;
	}

	.generic-promo-banner--flush {
		border-radius: 0;
	}

	.generic-promo-banner__scrim {
		position: absolute;
		inset: 0;
		background: linear-gradient(
			180deg,
			rgba(0, 0, 0, 0.15) 0%,
			rgba(0, 0, 0, 0.45) 55%,
			rgba(0, 0, 0, 0.62) 100%
		);
		pointer-events: none;
		z-index: 0;
	}

	.generic-promo-banner__body {
		position: relative;
		z-index: 1;
		display: flex;
		flex-direction: column;
		flex: 1;
		min-height: 0;
		padding: 16px 16px 44px;
		gap: 8px;
	}

	.generic-promo-banner--compact .generic-promo-banner__body {
		padding: 10px 12px 28px;
		gap: 4px;
	}

	.generic-promo-banner--compact .generic-promo-banner__cta-row {
		padding-top: 4px;
	}

	:global(.generic-promo-banner__title) {
		color: #ffffff !important;
		margin: 0;
		line-height: 1.3;
		max-width: 90%;
	}

	:global(.generic-promo-banner__message) {
		color: rgba(255, 255, 255, 0.92) !important;
		line-height: 1.4;
		max-width: 92%;
	}

	.generic-promo-banner__cta-row {
		display: flex;
		margin-top: auto;
		padding-top: 12px;
	}

	.generic-promo-banner__cta-row--bl {
		justify-content: flex-start;
	}

	.generic-promo-banner__cta-row--br {
		justify-content: flex-end;
	}

	.generic-promo-banner__cta-row--bc {
		justify-content: center;
	}

	.generic-promo-banner__cta-row--center {
		flex: 1;
		align-items: center;
		justify-content: center;
		margin-top: 0;
	}

	:global(.generic-promo-banner__cta) {
		background: rgba(255, 255, 255, 0.95) !important;
		color: #1a1a1a !important;
		border: none !important;
		border-radius: 8px !important;
		font-weight: 600 !important;
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2) !important;
	}
</style>
